export type Theme = 'light' | 'dark'

// Forced theme is decided entirely by the tenant's `dark_mode` feature flag
// in the tenancy DB (see fetchForcedTheme below). No per-brand hardcode —
// a brand with no flag row set just behaves normally (OS/localStorage).
const FORCED_THEME_FETCH_TIMEOUT_MS = 800

class ThemeManager {
  private currentTheme: Theme
  private storageKey = 'app-theme'
  private root: HTMLElement | null = null
  private forcedTheme: Theme | undefined

  constructor() {
    this.root = document.documentElement
    this.currentTheme = 'dark'
  }

  public async init(): Promise<void> {
    this.forcedTheme = await this.fetchForcedTheme()
    this.currentTheme = this.loadTheme()
    this.applyTheme(this.currentTheme)
    this.observeSystemTheme()
  }

  /**
   * Reads the tenant's forced color-scheme from the X-Theme-Mode header on
   * the same /theme.css request the app already makes for brand color
   * tokens (backed by the `dark_mode` feature flag in the tenancy DB).
   * Returns undefined (no forced theme) on error/timeout or when the tenant
   * has no flag row — the app then falls back to OS/localStorage preference.
   */
  private async fetchForcedTheme(): Promise<Theme | undefined> {
    try {
      const controller = new AbortController()
      const timeout = window.setTimeout(() => controller.abort(), FORCED_THEME_FETCH_TIMEOUT_MS)
      const response = await fetch('/theme.css', { method: 'GET', signal: controller.signal })
      window.clearTimeout(timeout)

      const mode = response.headers.get('X-Theme-Mode')
      return mode === 'light' || mode === 'dark' ? mode : undefined
    } catch {
      return undefined
    }
  }

  private loadTheme(): Theme {
    if (this.forcedTheme) {
      const themeOverride = new URLSearchParams(window.location.search).get('themeOverride')
      if (themeOverride === 'light' || themeOverride === 'dark') {
        return themeOverride
      }
      return this.forcedTheme
    }

    const savedTheme = localStorage.getItem(this.storageKey) as Theme | null
    if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      return savedTheme
    }

    if (
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: light)').matches
    ) {
      return 'light'
    }

    return 'dark'
  }

  private applyTheme(theme: Theme): void {
    if (!this.root) return

    this.currentTheme = theme
    this.root.setAttribute('data-theme', theme)
    localStorage.setItem(this.storageKey, theme)

    window.dispatchEvent(
      new CustomEvent('themechange', { detail: { theme } })
    )
  }


  private observeSystemTheme(): void {
    if (this.forcedTheme) return
    if (!window.matchMedia) return

    const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)')

    if (darkModeQuery.addEventListener) {
      darkModeQuery.addEventListener('change', (e) => {
        if (!localStorage.getItem(this.storageKey)) {
          this.setTheme(e.matches ? 'dark' : 'light')
        }
      })
    }
  }

  public setTheme(theme: Theme): void {
    if (this.forcedTheme) return
    this.applyTheme(theme)
  }

  public toggleTheme(): void {
    if (this.forcedTheme) return
    const newTheme = this.currentTheme === 'light' ? 'dark' : 'light'
    this.setTheme(newTheme)
  }


  public getTheme(): Theme {
    return this.currentTheme
  }

  public resetToSystem(): void {
    if (this.forcedTheme) return
    localStorage.removeItem(this.storageKey)
    const theme = this.loadTheme()
    this.applyTheme(theme)
  }


  public isDark(): boolean {
    return this.currentTheme === 'dark'
  }

  public isLight(): boolean {
    return this.currentTheme === 'light'
  }

  public syncFromDom(): void {
    const domTheme = this.root?.getAttribute('data-theme') as Theme | null
    if (domTheme === 'light' || domTheme === 'dark') {
      this.currentTheme = domTheme
    }
  }
}

export const themeManager = new ThemeManager()